import ChartDownloadButton from "./ChartDownloadButton";
import React from "react";
import V3PeriodFilterRow from "./V3PeriodFilterRow";
import V3PerformanceSummaryTable from "./V3PerformanceSummaryTable";
import type { PerformanceChartDataResult } from "../hooks/usePerformanceChartData";
import PerformanceChartSurface from "./PerformanceChartSurface";
import type { PerformanceChartVisualStyle } from "./performanceChartAppearance";

type PerformanceChartV3ContentProps = Pick<
  PerformanceChartDataResult,
  | "loading"
  | "error"
  | "performanceMetrics"
  | "svgRef"
  | "containerRef"
  | "tooltipRef"
  | "chartHeight"
  | "timeFilterOptions"
  | "availableFilters"
  | "selectedPeriod"
  | "handleSelectPeriod"
  | "formattedCreateDate"
  | "formatPerformance"
  | "formatLocalDate"
  | "orderedFiltersForTable"
  | "timeframePerformance"
  | "v3IntrinsicAvailability"
  | "v3LoadedCoverage"
  | "v3HasDataByPeriod"
  | "entityType"
> & {
  portfolioName?: string;
  showWatermark?: boolean;
  isDarkMode?: boolean;
  // When true, captions the period-performance summary table as gross of fees.
  // Used on the Discover detail page, whose "Net & Benchmark" chart view shows
  // the net-of-fees line. Off by default so other callers are unchanged.
  showGrossOfFeesNote?: boolean;
  appearance?: "default" | "alpha";
  visualStyle?: PerformanceChartVisualStyle;
  chartData?: PerformanceChartDataResult["chartData"];
};

const PerformanceChartV3Content: React.FC<PerformanceChartV3ContentProps> = ({
  loading,
  error,
  performanceMetrics,
  svgRef,
  containerRef,
  tooltipRef,
  chartHeight,
  timeFilterOptions,
  availableFilters,
  selectedPeriod,
  handleSelectPeriod,
  formattedCreateDate,
  formatPerformance,
  formatLocalDate,
  orderedFiltersForTable,
  timeframePerformance,
  v3IntrinsicAvailability,
  v3LoadedCoverage,
  v3HasDataByPeriod,
  entityType,
  portfolioName,
  showWatermark = true,
  isDarkMode = true,
  showGrossOfFeesNote = false,
  appearance = "default",
  visualStyle = "default",
  chartData,
}) => {
  const isAlpha = appearance === "alpha";
  const isRetro = visualStyle === "retro";
  const entityTypeLabel =
    entityType === "MANAGED_STRATEGY"
      ? "managed strategy"
      : entityType.toLowerCase();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className={`h-8 w-8 animate-spin rounded-full border-b-2 ${isAlpha ? "border-v7-bronze-dark" : isDarkMode ? "border-slate-400" : "border-gray-400"}`} />
        <span className={`ml-3 text-sm ${isAlpha ? "text-v7-ink/60" : isDarkMode ? "text-gray-300" : "text-gray-500"}`}>Loading performance data...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`border p-4 ${isAlpha ? "rounded-v7-panel border-v7-rust/30 bg-v7-rust/5" : `rounded-lg ${isDarkMode ? "border-red-800 bg-red-900/20" : "border-red-200 bg-red-50"}`}`}>
        <p className={`text-sm ${isAlpha ? "text-v7-rust" : isDarkMode ? "text-red-400" : "text-red-600"}`}>{error}</p>
      </div>
    );
  }

  if (performanceMetrics.length === 0) {
    return (
      <div className={`py-8 text-center ${isAlpha ? "text-v7-ink/60" : isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
        <p>No performance data available for this {entityTypeLabel}.</p>
      </div>
    );
  }

  return (
    <div className={`flex flex-col ${isRetro ? "mx-auto w-full max-w-[900px] bg-white p-2 text-gray-900" : isAlpha ? "text-v7-ink" : ""}`}>
      {isRetro ? (
        <PerformanceChartSurface
          portfolioName={portfolioName}
          containerRef={containerRef}
          svgRef={svgRef}
          tooltipRef={tooltipRef}
          selectedPeriod={selectedPeriod}
          showWatermark={showWatermark}
          legend={<span className="flex items-center gap-1.5 text-[11px] leading-3">
            <span className="w-3 border-t border-retro-ink" />
            Performance {typeof chartData?.[chartData.length - 1]?.performance === "number"
              ? formatPerformance(chartData[chartData.length - 1].performance)
              : ""}
          </span>}
        />
      ) : <div
        ref={containerRef}
        style={{ width: "100%", height: chartHeight, position: "relative", overflow: "visible" }}
      >
        <svg ref={svgRef} style={{ display: "block" }} />
        <div
          ref={tooltipRef}
          style={{
            position: "absolute",
            display: "none",
            backgroundColor: isDarkMode ? "#020617" : "#ffffff",
            border: `1px solid ${isDarkMode ? "#334155" : "#e2e8f0"}`,
            borderRadius: "0.375rem",
            padding: "8px 12px",
            pointerEvents: "none",
            zIndex: 1000,
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
          }}
        />
        {showWatermark && (
          <div className="absolute pointer-events-none select-none" style={{ left: 12, bottom: 86, zIndex: 5 }}>
            <img
              data-testid="performance-chart-watermark-wordmark"
              src={isDarkMode ? "/dark.png" : "/logo.png"}
              alt="Plutus"
              className="h-8 w-auto"
            />
          </div>
        )}
      </div>}

      <V3PeriodFilterRow
        actions={isRetro ? <ChartDownloadButton containerRef={containerRef} portfolioName={portfolioName} /> : undefined}
        timeFilterOptions={timeFilterOptions}
        availableFilters={availableFilters as string[]}
        selectedPeriod={selectedPeriod}
        handleSelectPeriod={handleSelectPeriod}
        v3IntrinsicAvailability={v3IntrinsicAvailability}
        v3LoadedCoverage={v3LoadedCoverage}
        v3HasDataByPeriod={v3HasDataByPeriod}
        isDarkMode={isDarkMode}
        appearance={appearance}
      />

      {formattedCreateDate && (
        <div className={`mt-2 text-center text-[11px] ${isAlpha ? "text-v7-ink/60" : isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
          Inception: {formatLocalDate(formattedCreateDate)}
        </div>
      )}

      {performanceMetrics.length > 0 && (
        <V3PerformanceSummaryTable
          orderedFiltersForTable={orderedFiltersForTable}
          timeframePerformance={timeframePerformance}
          formatPerformance={formatPerformance}
          isDarkMode={isDarkMode}
          showGrossOfFeesNote={showGrossOfFeesNote}
          appearance={appearance}
        />
      )}
    </div>
  );
};

export default PerformanceChartV3Content;
